import { Link } from "react-router-dom";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";
import { api } from "@/lib/api";
import { loadShopSession, clearShopSession } from "@/lib/shopAuth";
import { cartItemCount, loadCart } from "@/lib/cart";
import { useShopMe } from "@/lib/useShopMe";

function CartIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
      />
    </svg>
  );
}

export function ShopLayout({ children }: { children: React.ReactNode }) {
  const { siteName, logoUrl } = useCampsiteBranding();
  const session = loadShopSession();
  const { me } = useShopMe();
  const cartCount = cartItemCount(loadCart());

  function logout() {
    void api.post("/api/v1/public/shop/auth/logout").finally(() => {
      clearShopSession();
      window.location.href = "/login";
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-brand-50/40 via-surface-muted to-slate-100">
      <header className="sticky top-0 z-50 border-b border-white/20 bg-white/80 shadow-header backdrop-blur-xl backdrop-saturate-150">
        <div className="shop-page flex items-center justify-between gap-4 py-3 sm:py-4">
          <Link to="/" className="group flex min-w-0 items-center gap-3">
            {logoUrl ? (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-slate-200/80 transition group-hover:ring-brand-200">
                <img src={logoUrl} alt="" className="h-full w-full object-contain" />
              </div>
            ) : (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-600 text-sm font-bold text-white shadow-sm">
                {siteName.charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-xs font-medium uppercase tracking-wider text-brand-600">
                {siteName}
              </p>
              <p className="truncate text-base font-bold text-slate-900 sm:text-lg">Gäste-Shop</p>
            </div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            {session ? (
              <>
                <div className="hidden text-right text-sm md:block">
                  <p className="font-semibold text-slate-900">
                    {session.guest.first_name} {session.guest.last_name}
                  </p>
                  {session.stay.pitch_code ? (
                    <p className="text-slate-500">Platz {session.stay.pitch_code}</p>
                  ) : null}
                </div>

                <Link
                  to="/warenkorb"
                  className="shop-btn-secondary relative !px-3 !py-2.5"
                  aria-label={`Warenkorb${cartCount > 0 ? `, ${cartCount} Artikel` : ""}`}
                >
                  <CartIcon />
                  <span className="hidden sm:inline">Warenkorb</span>
                  {cartCount > 0 ? (
                    <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white shadow-sm">
                      {cartCount}
                    </span>
                  ) : null}
                </Link>

                {me?.permanent_contract ? (
                  <Link to="/besucher" className="shop-btn-ghost hidden !px-3 !py-2.5 sm:inline-flex">
                    Besucher
                  </Link>
                ) : null}

                <button type="button" onClick={logout} className="shop-btn-ghost !px-3 !py-2.5 text-xs sm:text-sm">
                  Abmelden
                </button>
              </>
            ) : null}
          </div>
        </div>
      </header>

      <main className="flex-1 py-6 sm:py-8">{children}</main>
    </div>
  );
}
